import React, { useState, useMemo } from 'react';
import CollectionPreferences, {
	CollectionPreferencesProps,
} from '@amzn/awsui-components-console/collection-preferences';
import Header from '@amzn/awsui-components-console/header';
import Pagination from '@amzn/awsui-components-console/pagination';
import TextFilter from '@amzn/awsui-components-console/text-filter';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Modal from '@amzn/awsui-components-console/modal';
import Popover from '@amzn/awsui-components-console/popover';
import Link from '@amzn/awsui-components-console/link';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import * as tokens from '@amzn/awsui-design-tokens';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';
import { TaskDefinition } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import {
	ReactFlowHelpNodeIDList,
	isTerminalTaskId,
} from 'src/components/createWorklow/customNode/customNodeEnums';
import { TableWithNoData } from 'src/components/commonComponents/table/tableWithNoData';
import { useAuthContext } from 'src/providers/AuthContext';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';
import {
	COLUMN_DEFINITIONS,
	DEFAULT_PREFERENCES,
	COLLECTION_PREFERENCES,
	getTaskDependencyNames,
	makeTerminalRow,
} from './WorkflowTaskTableConstants';
import AddTaskModal from './AddTaskModal';
import DependencyEditor from './DependencyEditor';

export interface WorkflowTaskTableProps {
	onTaskSelect: (taskId: string) => void;
	onTaskEdit: (taskId: string) => void;
	selectedTaskId?: string;
}

export default function WorkflowTaskTable({
	onTaskSelect,
	onTaskEdit,
	selectedTaskId,
}: WorkflowTaskTableProps) {
	const [addTaskModalVisible, setAddTaskModalVisible] = useState(false);
	const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
	const [preferences, setPreferences] = useState<
		Partial<CollectionPreferencesProps.Preferences>
	>(() => ({
		...DEFAULT_PREFERENCES,
		stripedRows: getDefaultStripedRows(),
	}));

	const { taskDefinitions, dependencies } = useStore(
		(state: Store) => state.WFDefinition
	);
	const deleteTaskById = useStore((state: Store) => state.deleteTaskById);
	const duplicateTaskById = useStore((state: Store) => state.duplicateTaskById);
	const authContext = useAuthContext();
	const userName = authContext.userName as string;

	const regularTasks = useMemo(
		() => taskDefinitions.filter((task) => !isTerminalTaskId(task.taskId)),
		[taskDefinitions]
	);

	const tableTasks = useMemo(() => {
		return [
			makeTerminalRow(
				ReactFlowHelpNodeIDList.START,
				'START',
				'Workflow entry point'
			),
			...regularTasks,
			makeTerminalRow(
				ReactFlowHelpNodeIDList.END,
				'END',
				'Workflow exit point'
			),
		];
	}, [regularTasks]);

	const columnDefinitions = useMemo(() => {
		return COLUMN_DEFINITIONS.map((col) => {
			if (col.id === 'taskName') {
				return {
					...col,
					cell: (item: TaskDefinition) => {
						if (item.taskId === ReactFlowHelpNodeIDList.START) {
							return (
								<StatusIndicator type="pending">
									<strong>START</strong>
								</StatusIndicator>
							);
						}

						if (item.taskId === ReactFlowHelpNodeIDList.END) {
							return (
								<StatusIndicator type="stopped">
									<strong>END</strong>
								</StatusIndicator>
							);
						}

						return item.taskName;
					},
				};
			}

			if (col.id === 'dependencies') {
				return {
					...col,
					cell: (item: TaskDefinition) => {
						if (item.taskId === ReactFlowHelpNodeIDList.START) {
							return '-';
						}

						const depNames = getTaskDependencyNames(
							item.taskId,
							dependencies,
							taskDefinitions
						);

						return (
							<Popover
								dismissButton={false}
								position="bottom"
								size="large"
								triggerType="custom"
								content={
									<DependencyEditor
										taskId={item.taskId}
										dependencies={dependencies}
										allTasks={regularTasks}
									/>
								}
							>
								<Link variant="secondary">
									{depNames || (
										<span style={{ color: tokens.colorTextStatusError }}>Edit dependencies</span>
									)}
								</Link>
							</Popover>
						);
					},
				};
			}

			return col;
		});
	}, [dependencies, taskDefinitions, regularTasks]);

	const selectedItems = useMemo(() => {
		if (!selectedTaskId) return [];
		const found = regularTasks.find((t) => t.taskId === selectedTaskId);

		return found ? [found] : [];
	}, [selectedTaskId, regularTasks]);

	const {
		items,
		filteredItemsCount,
		collectionProps,
		filterProps,
		paginationProps,
	} = useCollection(tableTasks, {
		filtering: {
			empty: <TableWithNoData message="No tasks in this workflow." />,
			noMatch: <TableWithNoData message="No tasks match your search query." />,
		},
		pagination: { pageSize: preferences.pageSize },
		sorting: {},
	});

	function filterCounter(count: number) {
		return `${count} ${count === 1 ? 'match' : 'matches'}`;
	}

	return (
		<Table
			{...collectionProps}
			resizableColumns
			stickyHeader
			columnDefinitions={columnDefinitions}
			items={items}
			wrapLines
			stripedRows={preferences.stripedRows}
			trackBy="taskId"
			header={
				<Box padding={{ top: 'xxs' }}>
					<Header
						variant="h2"
						counter={`(${filteredItemsCount})`}
						actions={
							<SpaceBetween direction="horizontal" size="xs">
								<Button
									disabled={!selectedTaskId}
									onClick={() => {
										if (selectedTaskId) {
											setDeleteConfirmVisible(true);
										}
									}}
								>
									Delete
								</Button>
								<Button
									disabled={!selectedTaskId}
									onClick={() => {
										if (selectedTaskId) {
											const newTaskId = duplicateTaskById(
												selectedTaskId,
												userName
											);

											if (newTaskId) {
												onTaskSelect(newTaskId);
											}
										}
									}}
								>
									Duplicate
								</Button>
								<Button
									disabled={!selectedTaskId}
									onClick={() => {
										if (selectedTaskId) {
											onTaskEdit(selectedTaskId);
										}
									}}
								>
									Edit
								</Button>
								<Button onClick={() => setAddTaskModalVisible(true)}>
									Add Task
								</Button>
							</SpaceBetween>
						}
					>
						Workflow Tasks
					</Header>
					<AddTaskModal
						visible={addTaskModalVisible}
						onDismiss={() => setAddTaskModalVisible(false)}
						onAdd={(_taskType, newTaskId) => {
							onTaskSelect(newTaskId);
						}}
					/>
					<Modal
						visible={deleteConfirmVisible}
						onDismiss={() => setDeleteConfirmVisible(false)}
						header="Delete task"
						footer={
							<Box float="right">
								<SpaceBetween direction="horizontal" size="xs">
									<Button
										variant="link"
										onClick={() => setDeleteConfirmVisible(false)}
									>
										Cancel
									</Button>
									<Button
										variant="primary"
										onClick={() => {
											if (selectedTaskId) {
												deleteTaskById(selectedTaskId);
												onTaskSelect('');
											}

											setDeleteConfirmVisible(false);
										}}
									>
										Delete
									</Button>
								</SpaceBetween>
							</Box>
						}
					>
						Are you sure you want to delete <b>{regularTasks.find((t) => t.taskId === selectedTaskId)?.taskName}</b>? This action cannot be
						undone.
					</Modal>
				</Box>
			}
			columnDisplay={preferences.contentDisplay}
			pagination={<Pagination {...paginationProps} />}
			selectionType="single"
			isItemDisabled={(item) => isTerminalTaskId(item.taskId)}
			selectedItems={selectedItems}
			onSelectionChange={({ detail }) => {
				const selected = detail.selectedItems[0];

				if (selected && !isTerminalTaskId(selected.taskId)) {
					onTaskSelect(selected.taskId);
				}
			}}
			onRowClick={({ detail }) => {
				if (!isTerminalTaskId(detail.item.taskId)) {
					onTaskSelect(detail.item.taskId);
				}
			}}
			preferences={
				<CollectionPreferences
					{...COLLECTION_PREFERENCES}
					preferences={preferences}
					onConfirm={({ detail }) => setPreferences(detail)}
				/>
			}
			filter={
				<TextFilter
					{...filterProps}
					filteringPlaceholder="Search tasks"
					countText={filterCounter(filteredItemsCount ?? 0)}
				/>
			}
		/>
	);
}
